import { getCurrentIdToken, memoizedRefreshOAuthToken, refreshOrLogout } from '@/services/auth/oauthService';
import axios, { AxiosError, AxiosRequestConfig } from 'axios';

interface ExtendedAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean;
}

const axiosClient = axios.create();

axiosClient.interceptors.request.use(
  (config) => {
    if (config.withCredentials !== false) {
      const token = getCurrentIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

axiosClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as ExtendedAxiosRequestConfig;

    if (
      originalRequest &&
      originalRequest.withCredentials !== false &&
      (error.response?.status === 401 || error.response?.status === 403) &&
      !originalRequest._retry
    ) {
      console.log('🔒 Token expired, attempting to refresh...');
      originalRequest._retry = true;

      // Use shared memoized refresh to prevent racing with GraphQL interceptor
      const success = await memoizedRefreshOAuthToken();
      if (success) {
        const newToken = getCurrentIdToken();
        if (newToken && originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return axiosClient.request(originalRequest);
      }

      // Memoized refresh failed — try one more time then logout
      console.error('🔒 Token refresh failed, redirecting to sign-in page');
      await refreshOrLogout();
      return Promise.reject(error);
    }

    return Promise.reject(error);
  },
);

export async function fetchRestData<T = any>(endpoint: string, options: AxiosRequestConfig = {}) {
  try {
    const response = await axiosClient({
      url: endpoint,
      method: options.method || 'GET',
      ...options,
    });
    return response.data;
  } catch (error) {
    console.log(error);
  }
}
